import clsx from 'clsx';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { Table } from '@dynamika-shared/ui/Table/Table';
import { makeClinicalTrialsColumns } from './ClinicalTrialsTable.columns';
import { FC, ReactNode, useState } from 'react';
import { ClinicalTrialType } from '@dynamika-shared/types/clinicalTrials';
import {
  OnSortColumnType,
  Pagination,
} from '@dynamika-shared/ui/Table/Table.types';
import { ClinicalTrialCreate } from '@components/modals/ClinicalTrialCreate/ClinicalTrialCreate';
import { useNavigate } from 'react-router-dom';
import { routesPath } from '@router/routes';
import { NoContent } from '@dynamika-shared/ui/NoContent/NoContent';
import { prepareUrl } from '@utils/routerHelpers';
import { useAuthSlice } from '@restore/auth/auth.slice';
import { UserRoles } from '@dynamika-shared/types/auth';

type ClinicalTrialListPageViewProps = {
  loading?: boolean;
  clinicalTrialsData: ClinicalTrialType[];
  totalItems: number;
  onSort: OnSortColumnType;
  sortState?: string;
  pagination: Pagination;
  children?: ReactNode;
};

export const ClinicalTrialListPageView: FC<ClinicalTrialListPageViewProps> = ({
  clinicalTrialsData,
  totalItems,
  loading,
  onSort,
  sortState,
  pagination,
  children,
}) => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);

  const selectedRole = useAuthSlice(s => s?.data?.selectedRole) as
    | UserRoles
    | undefined;

  const isPM = selectedRole === UserRoles.PROJECT_MANAGER;

  const isSupport = selectedRole === UserRoles.SUPPORT;

  const clinicalTrialsColumns = makeClinicalTrialsColumns({
    onClinicalTrialClick: ct => {
      navigate(
        isSupport
          ? routesPath.clinicalTrialSubjectList.replace(':id', ct.id)
          : routesPath.clinicalTrialGeneralInformation.replace(':id', ct.id)
      );
    },
  });

  return (
    <div data-testid='ClinicalTrialListPageView' className={clsx('h-full')}>
      {showForm && (
        <ClinicalTrialCreate
          toggleVisibility={setShowForm}
          isVisible={showForm}
        />
      )}

      <div className='mini:py-2.5 flex items-center justify-between py-4.5'>
        <h1 className='mini:text-1824 w-full'>Clinical Trials</h1>
        <div className='flex justify-end w-full mb-1 mr-2 pr-2'>{children}</div>
        {!isPM && (
          <Button
            iconType='plus'
            iconClassName={'w-2 h-2'}
            type='primary'
            onClick={() => setShowForm(true)}
            className='mini:!text-1420 mini:!py-1'
          >
            Clinical Trial
          </Button>
        )}
      </div>

      <div className='relative h-[calc(100%-120px)]'>
        <Table
          columns={clinicalTrialsColumns}
          data={clinicalTrialsData}
          totalItems={totalItems}
          loading={loading}
          onSort={onSort}
          sortState={sortState}
          pagination={pagination}
          onRowClick={row => {
            if (typeof row !== 'number') {
              navigate(
                isSupport
                  ? prepareUrl(routesPath.clinicalTrialSubjectList, [row.id])
                  : prepareUrl(routesPath.clinicalTrialGeneralInformation, [
                      row.id,
                    ])
              );
            }
          }}
          noContentMessage={
            <NoContent
              action={() => setShowForm(true)}
              actionText={'Add Clinical Trial'}
              iconName={'clinicalTrials'}
              title={
                <span className='text-1424 text-spanishGray'>
                  No{' '}
                  <span className='font-semibold text-chineseBlack'>
                    Clinical Trials
                  </span>{' '}
                  here yet! <br /> Click the button to create a Clinical Trial!
                </span>
              }
            />
          }
        />
      </div>
    </div>
  );
};
